import { ChevronLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Divider } from '@mui/joy';
import * as Popover from '@radix-ui/react-popover';

import {
  ReactNode,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { KeyboardShortcut } from '@/Pages/KeyboardShortcutsSidePane';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { useOrganizationId, useSelectedTenant } from '@/hooks/useSwr';
import CheckIcon from '@/icons/CheckIcon.svg?react';
import { FeedbackConfigSchema, FeedbackSchema } from '@/types/schema';
import { getColorByString } from '@/utils/get-color-by-string';
import { cn } from '@/utils/tailwind';
import { tryParseJSON } from '@/utils/try-parse-json';

import { FeedbackConfigForm } from '../FeedbackConfig/FeedbackConfigForm';
import { RunAnnotationsSingleFeedbackForm } from './RunAnnotationsSingleFeedbackForm';
import { RunNotesCrudPanel } from './RunNotesCrudPanel';
import { useSortedFeedbackConfigs } from './hooks/useSortedFeedbackConfigs';

interface FormProps {
  runId: string;
  sessionId: string;
  runFeedbacks: FeedbackSchema[];
  userId: string | null;
  className?: string;
  description?: string;
  onFeedbackGiven?: () => void;
  configs: FeedbackConfigSchema[];
}

export function FeedbackConfigAddNewButton(props: {
  preselected: Set<string>;
  value: string[];
  onChange: (value: string[]) => void;
  children: ReactNode;
}) {
  const configs = useSortedFeedbackConfigs();
  const [view, setView] = useState<'select' | 'new' | 'hidden'>('hidden');
  const addTagsCommandInputRef = useRef<HTMLInputElement>(null);

  const tenant = useSelectedTenant();

  useHotkeys(
    'esc',
    (event) => {
      if (view === 'new') {
        setView('select');
      } else if (view === 'select') {
        event.stopPropagation();
        setView('hidden');
      }
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  return (
    <Popover.Root
      open={view !== 'hidden'}
      onOpenChange={(open) => {
        setView(open ? 'select' : 'hidden');
      }}
    >
      <Popover.Trigger asChild>{props.children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={8} className="relative z-10">
          <div className="max-w-[420px] rounded-lg border border-secondary bg-popover shadow-lg">
            {view === 'select' && (
              <Command>
                <div className="flex items-center justify-between gap-2">
                  <CommandInput
                    placeholder="Add tags..."
                    className="border-none"
                    ref={addTagsCommandInputRef}
                  />
                  <button
                    type="button"
                    role="button"
                    className="my-2 mr-2 flex items-center gap-2 rounded-md border border-secondary p-2 py-1 text-sm hover:bg-secondary-hover active:bg-secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      setView('new');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setView('new');
                      }
                    }}
                  >
                    <PlusIcon className="h-4 w-4" />
                    <span>New</span>
                  </button>
                </div>
                <CommandList>
                  <CommandGroup
                    className="m-0 overflow-y-auto [&_[cmdk-group-heading]]:bg-secondary [&_[cmdk-group-heading]]:px-3.5 [&_[cmdk-group-items]]:py-1.5"
                    heading={
                      <span className="text-xs font-medium uppercase tracking-wide text-tertiary">
                        {tenant.data?.display_name}
                      </span>
                    }
                  >
                    {configs
                      ?.filter((i) => i.feedback_config.type !== 'freeform')
                      ?.map((config) => {
                        const preselected = props.preselected.has(
                          config.feedback_key
                        );

                        const selected = props.value.includes(
                          config.feedback_key
                        );
                        return (
                          <CommandItem
                            className="mx-1.5 flex cursor-pointer items-center px-2 py-2"
                            key={config.feedback_key}
                            onSelect={() => {
                              if (preselected) return;
                              if (props.value.includes(config.feedback_key)) {
                                props.onChange(
                                  props.value.filter(
                                    (i) => i !== config.feedback_key
                                  )
                                );
                              } else {
                                props.onChange([
                                  ...props.value,
                                  config.feedback_key,
                                ]);
                              }
                            }}
                          >
                            <span className="flex items-center gap-3">
                              <div
                                className={cn(
                                  'text-brand flex h-4 w-4 items-center justify-center rounded-[4px] border border-secondary',
                                  (preselected || selected) &&
                                    'bg-brand border-brand',
                                  preselected && 'opacity-50'
                                )}
                              >
                                {(preselected || selected) && (
                                  <CheckIcon
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                  />
                                )}
                              </div>

                              <span className="flex items-center gap-2">
                                <div
                                  className="h-2 w-2 shrink-0 rounded-sm"
                                  style={{
                                    backgroundColor: getColorByString(
                                      config.feedback_key
                                    ),
                                  }}
                                />
                                <span className="text-xs font-medium uppercase leading-none">
                                  {config.feedback_key}
                                </span>
                              </span>
                            </span>
                          </CommandItem>
                        );
                      })}
                  </CommandGroup>
                </CommandList>
              </Command>
            )}

            {view === 'new' && (
              <>
                <div className="flex items-center gap-1 py-3 pl-2.5 pr-4">
                  <button
                    aria-label="Back"
                    type="button"
                    className="rounded-md p-1 hover:bg-secondary-hover active:bg-secondary"
                    onClick={() => setView('select')}
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-sm font-medium">New Feedback Tag</span>
                </div>
                <div className="p-4 pt-0">
                  <FeedbackConfigForm
                    onSuccess={(config) => {
                      props.onChange([...props.value, config.feedback_key]);
                      setView('select');
                    }}
                    defaultTagText={addTagsCommandInputRef.current?.value}
                  />
                </div>
              </>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function useArrayOfRefs<T extends HTMLElement>(length: number) {
  return useRef<Array<React.RefObject<T>>>(
    Array(length)
      .fill(null)
      .map(() => createRef<T>())
  );
}

function simulateEnterKeyPress(element) {
  const enterKeyEvent = new KeyboardEvent('keydown', {
    key: 'Enter',
    bubbles: true,
    cancelable: true,
  });
  element.dispatchEvent(enterKeyEvent);
}

export function RunAnnotationsCrudForm({
  runId,
  sessionId,
  runFeedbacks,
  userId,
  className,
  description,
  onFeedbackGiven,
  configs,
}: FormProps) {
  const addTagButtonRef = useRef<HTMLButtonElement>(null);
  const annotationsPaneRef = useRef<HTMLDivElement>(null);

  // make sure that the feedback key doesn't disappear when it's removed
  const lastFeedbackSet = useRef<Set<string>>(new Set(['correctness']));
  const derivedFeedbackKeys = useMemo(
    () => new Set(['correctness', ...runFeedbacks.map((i) => i.key)]),
    [runFeedbacks]
  );

  const organizationId = useOrganizationId();
  const [localFeedbackKeys, innerSetLocalFeedbackKeys] = useState<string[]>(
    () =>
      tryParseJSON(
        window.localStorage.getItem(`ls:localFeedbackKeys:${organizationId}`) ??
          ''
      ) ?? []
  );

  const setLocalFeedbackKeys = useCallback(
    (value: string[] | ((value: string[]) => string[])) => {
      innerSetLocalFeedbackKeys((oldValues) => {
        const newValues =
          typeof value === 'function' ? value(oldValues) : value;

        window.localStorage.setItem(
          `ls:localFeedbackKeys:${organizationId}`,
          JSON.stringify(newValues)
        );

        return newValues;
      });
    },
    [innerSetLocalFeedbackKeys, organizationId]
  );

  const activeFeedbackKeys = new Set([
    ...derivedFeedbackKeys,
    ...localFeedbackKeys,
  ]);

  const feedbackConfigs = configs?.filter(
    (config) =>
      config.feedback_config.type !== 'freeform' &&
      activeFeedbackKeys.has(config.feedback_key)
  );

  useEffect(() => {
    // if previously shown feedback and not present in the derived feedback set,
    // move it to the local feedback keys
    const missingFeedbackKeys = [...lastFeedbackSet.current].filter(
      (i) => !derivedFeedbackKeys.has(i)
    );
    setLocalFeedbackKeys((keys) => [
      ...new Set<string>([...keys, ...missingFeedbackKeys]),
    ]);

    lastFeedbackSet.current = derivedFeedbackKeys;
  }, [derivedFeedbackKeys, setLocalFeedbackKeys]);

  const findFeedbackFromCurrentUser = useCallback(
    (feedbackKey: string) =>
      runFeedbacks?.find(
        (f) =>
          f.key === feedbackKey &&
          f.feedback_source.user_id === userId &&
          f.feedback_source.type === 'app'
      ),
    [runFeedbacks, userId]
  );

  const isMac = useMemo(
    () => /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent),
    []
  );

  useHotkeys('t', (event) => {
    event.preventDefault();
    addTagButtonRef.current?.click();
    if (annotationsPaneRef.current) {
      annotationsPaneRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  });

  const refs = useArrayOfRefs<HTMLDivElement>(10);

  const handleFeedbackHotkeys = (event, idx) => {
    const elt = refs.current && refs.current[idx - 1]?.current;
    if (!elt) return;

    const feedbackConfig = feedbackConfigs?.[idx - 1]?.feedback_config;

    if (!feedbackConfig) return;

    if (feedbackConfig.type === 'continuous' && !feedbackConfig.categories) {
      const inputElement = (elt as HTMLElement).querySelector('input');
      if (inputElement) {
        event?.preventDefault();
        inputElement.focus();
      } else {
        (elt as HTMLElement).focus();
      }
    } else if (
      feedbackConfig.type === 'categorical' ||
      feedbackConfig.categories
    ) {
      const categories = feedbackConfig.categories as Array<{
        value: number;
        label?: string;
      }> | null;
      if (!categories || categories.length <= 2) {
        const divElt: HTMLDivElement | null = (
          elt as HTMLElement
        ).querySelector('[id^="select-feedback"]');

        if (divElt) {
          event?.preventDefault();
          divElt.focus();
        } else {
          (elt as HTMLElement).focus();
        }
      } else {
        const buttonElt = (elt as HTMLElement).querySelector('button');
        if (buttonElt) {
          simulateEnterKeyPress(buttonElt);
        } else {
          (elt as HTMLElement).focus();
        }
      }
    } else {
      // Should not happen if we only have categorical and continuous feedback
      (elt as HTMLElement).focus();
    }
  };

  useHotkeys(
    'alt+1, alt+2, alt+3, alt+4, alt+5, alt+6, alt+7, alt+8, alt+9',
    (event) => {
      const idx = Number(event.code[event.code.length - 1]);
      handleFeedbackHotkeys(event, idx);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  return (
    <div className="h-full bg-primary">
      <div
        ref={annotationsPaneRef}
        className={cn(
          'flex flex-col items-start self-stretch bg-inherit p-5',
          className
        )}
      >
        {description && (
          <>
            <div className="font-normal leading-snug text-tertiary">
              {description}
            </div>
            <div className="h-5" />
          </>
        )}

        <div className="flex flex-col gap-4 self-stretch">
          <div className="flex items-center justify-between">
            <div className="font-semibold">Feedback</div>
            <FeedbackConfigAddNewButton
              preselected={derivedFeedbackKeys}
              value={localFeedbackKeys}
              onChange={setLocalFeedbackKeys}
            >
              <button
                ref={addTagButtonRef}
                type="button"
                className="-mr-1.5 flex flex-row rounded-md border border-secondary px-2 py-1 text-sm transition-all hover:bg-primary-hover active:bg-secondary data-[state=open]:bg-secondary"
              >
                <span className="my-auto">Add tag</span>
                <KeyboardShortcut keys={['T']} />
              </button>
            </FeedbackConfigAddNewButton>
          </div>
          <div className="flex flex-col">
            {feedbackConfigs?.map((config, idx) => (
              <div
                className={cn(
                  'flex items-center justify-between gap-3 py-2',
                  idx < feedbackConfigs.length - 1 &&
                    'border-b border-secondary'
                )}
                key={config.feedback_key}
                id={`feedback-${idx}`}
                ref={refs.current[idx]}
              >
                <div className="my-auto flex items-center gap-2">
                  <div
                    className="h-2 w-2 shrink-0 rounded-sm"
                    style={{
                      backgroundColor: getColorByString(config.feedback_key),
                    }}
                  />
                  <div
                    data-testid="simple-run-annotation-tag"
                    className="line-clamp-1 grow-0 overflow-hidden break-words break-all text-center text-sm font-medium uppercase leading-none"
                  >
                    {config.feedback_key}
                  </div>
                  <KeyboardShortcut
                    keys={[
                      isMac ? (
                        <span className="text-[14px]">⌥</span>
                      ) : (
                        <span className="-tracking-[0.1em]">Alt</span>
                      ),
                      `${idx + 1}`,
                    ]}
                    className={cn('my-auto ml-0 mr-8', idx > 8 && 'hidden')}
                  />
                </div>

                <div
                  className="flex shrink-0 flex-col items-end"
                  data-testid={`feedback-input-${config.feedback_key}`}
                >
                  <RunAnnotationsSingleFeedbackForm
                    runId={runId}
                    config={config}
                    feedback={findFeedbackFromCurrentUser(config.feedback_key)}
                    onSuccess={() => {
                      onFeedbackGiven?.();
                    }}
                    idx={idx}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="h-6" />

        <Divider />

        <div className="h-6" />

        <div className="flex flex-col self-stretch">
          <RunNotesCrudPanel runId={runId} sessionId={sessionId} />
        </div>
      </div>
    </div>
  );
}
